import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { HexIcon } from '@/components/ui/hex-icon';
import { useAuth } from '@/contexts/auth-context';
import { 
  Zap, 
  User, 
  LogOut, 
  LogIn, 
  Settings, 
  Menu,
  X
} from 'lucide-react';

interface HeaderProps {
  showNavigation?: boolean;
  className?: string;
  onSettingsClick?: () => void;
}

export const Header: React.FC<HeaderProps> = ({ 
  showNavigation = true, 
  className = "",
  onSettingsClick
}) => {
  const { user, signOut, loading } = useAuth();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    setIsMobileMenuOpen(false);
  };

  // Determine navigation links based on current page
  const getNavigationLinks = () => {
    if (!user) {
      return [{ to: `/${window.location.search}`, label: 'Home' }];
    }

    const currentPath = location.pathname;
    const links = [];

    // Only show Home link if not on home, flow, or chat pages
    if (currentPath !== '/' && currentPath !== '/index' && currentPath !== '/plan' && currentPath !== '/chat') {
      links.push({ to: `/${window.location.search}`, label: 'Home' });
    }

    // Always show Flow and Chat for authenticated users
    links.push({ to: `/plan${window.location.search}`, label: 'Flow' });
    links.push({ to: `/chat${window.location.search}`, label: 'Chat' });

    return links;
  };

  const navigationLinks = getNavigationLinks();

  const NavLinks = () => (
    <>
      {navigationLinks.map((link) => (
        <Link
          key={link.to}
          to={link.to}
          className="text-foreground hover:text-primary transition-colors font-medium"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          {link.label}
        </Link>
      ))}
    </>
  );

  const AuthSection = () => (
    <>
      {loading ? (
        <div className="w-8 h-8 animate-pulse bg-muted rounded"></div>
      ) : user ? (
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-2 text-sm">
            <User className="w-4 h-4" />
            <span className="text-foreground">{user.display_name || user.email}</span>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleSignOut}
            className="gap-1"
          >
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline">Sign Out</span>
          </Button>
          {onSettingsClick && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={onSettingsClick}
              className="gap-1"
              title="Workspace Settings"
            >
              <Settings className="w-4 h-4" />
              <span className="hidden sm:inline">Settings</span>
            </Button>
          )}
        </div>
      ) : (
        <Link to="/auth">
          <Button variant="default" size="sm" className="gap-1">
            <LogIn className="w-4 h-4" />
            <span className="hidden sm:inline">Sign In</span>
          </Button>
        </Link>
      )}
    </>
  );

  return (
    <header className={`sticky top-0 z-50 border-b bg-card/50 backdrop-blur-sm ${className}`}>
      <div className="hex-container py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <HexIcon icon={Zap} size="small" className="bg-primary/20" />
            <span className="text-xl font-bold text-foreground">WattFactory</span>
          </div>

          {/* Desktop Navigation */}
          {showNavigation && (
            <nav className="hidden md:flex items-center gap-6">
              <NavLinks />
              <AuthSection />
            </nav>
          )}

          {/* Mobile Menu */}
          {showNavigation && (
            <div className="md:hidden">
              <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="sm" className="p-2">
                    <Menu className="w-5 h-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                  <div className="flex flex-col h-full">
                    {/* Mobile Header */}
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-2">
                        <HexIcon icon={Zap} size="small" className="bg-primary/20" />
                        <span className="text-xl font-bold text-foreground">WattFactory</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="p-2"
                      >
                        <X className="w-5 h-5" />
                      </Button>
                    </div>

                    {/* Mobile Navigation */}
                    <nav className="flex flex-col gap-4 mb-6">
                      <NavLinks />
                    </nav>

                    {/* Mobile Auth Section */}
                    <div className="mt-auto">
                      {loading ? (
                        <div className="w-full h-10 animate-pulse bg-muted rounded"></div>
                      ) : user ? (
                        <div className="flex flex-col gap-3">
                          <div className="flex items-center gap-2 text-sm p-3 bg-muted rounded-lg">
                            <User className="w-4 h-4" />
                            <span className="text-foreground">{user.display_name || user.email}</span>
                          </div>
                          <Button 
                            variant="outline" 
                            onClick={handleSignOut}
                            className="gap-2 justify-start"
                          >
                            <LogOut className="w-4 h-4" />
                            Sign Out
                          </Button>
                          {onSettingsClick && (
                            <Button 
                              variant="outline"
                              className="gap-2 justify-start"
                              onClick={onSettingsClick}
                              title="Workspace Settings"
                            >
                              <Settings className="w-4 h-4" />
                              Settings
                            </Button>
                          )}
                        </div>
                      ) : (
                        <Link to="/auth" className="w-full">
                          <Button variant="default" className="w-full gap-2 justify-start">
                            <LogIn className="w-4 h-4" />
                            Sign In
                          </Button>
                        </Link>
                      )}
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          )}

          {/* Desktop Auth Section (when navigation is hidden) */}
          {!showNavigation && (
            <div className="hidden md:block">
              <AuthSection />
            </div>
          )}
        </div>
      </div>
    </header>
  );
}; 